import { useState } from 'react';

import { enterConfirms } from '../lib/keyboard.js';
import { Button, Modal, Textarea } from '../components/ui.js';

/**
 * What the customer asked for on one item.
 *
 * "No walnuts", "chocolate instead of almond", "cut in half" — the things a
 * bakery hears constantly and which, written on the order rather than
 * remembered, are what stop the wrong loaf going out.
 *
 * Shared by the till, the order-from-chat sheet and the edit screen, because
 * the same request arrives through all three and a note that only exists in
 * one of them is a note the baker cannot rely on.
 *
 * It travels with the line onto the printed receipt and into the customer's
 * confirmation message, so it is written for them to read rather than as
 * shorthand for the kitchen.
 */
export function ItemNoteModal({
  line,
  onClose,
  onSave,
}: {
  line: { name: string; notes?: string | null };
  onClose: () => void;
  onSave: (notes: string) => void;
}) {
  const [text, setText] = useState(line.notes ?? '');

  return (
    <Modal
      open
      onClose={onClose}
      title={line.name}
      description="Anything the customer asked for on this item. It prints on the receipt and goes in their confirmation."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {/* Separate from Cancel on purpose: backing out and taking a note
              off are different intentions, and one should not do the other. */}
          {line.notes && (
            <Button variant="ghost" onClick={() => onSave('')}>
              Remove note
            </Button>
          )}
          <Button variant="primary" onClick={() => onSave(text)}>
            Save note
          </Button>
        </>
      }
    >
      <Textarea
        rows={4}
        autoFocus
        value={text}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          /*
           * At a desk, Enter saves and Shift+Enter makes a line. On a phone it
           * only makes a line and "Save note" saves — a note genuinely needs a
           * second line sometimes ("No walnuts. Cut in half.") and a touch
           * keyboard has no Shift to pair with its return key. Same rule as
           * the Inbox composer; see `enterConfirms`.
           */
          if (enterConfirms() && event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            onSave(text);
          }
        }}
        placeholder="No walnuts, please. Cut in half."
        className="resize-y"
      />
    </Modal>
  );
}
